<template>
  <!-- The video side of writing a note: pick a video (it uploads at once), see
       each one's progress, and say per video who may play it. A video is never
       played here: nothing in this box makes an object url or an element. -->
  <div
    class="note-video-attach"
    data-test="note-video-attach"
  >
    <div class="note-video-bar">
      <button
        type="button"
        class="note-video-button"
        data-test="note-video-attach-button"
        :disabled="full"
        :title="full ? 'This note already has the most videos it can take.' : 'Attach a video to this note'"
        @click="choose"
      >
        <font-awesome-icon :icon="['fas', 'file-video']" />
        Attach video
      </button>
      <input
        ref="input"
        type="file"
        multiple
        hidden
        data-test="note-video-input"
        :accept="accept"
        @change="picked"
      >
      <span
        class="note-video-hint"
        data-test="note-video-hint"
      >
        A video uploads as soon as you pick it and goes with the note when you press Save. A saved draft keeps the text only.
      </span>
    </div>
    <div
      v-if="entries.length"
      class="note-video-list"
      data-test="note-video-list"
    >
      <div
        v-for="entry in entries"
        :key="entry.id"
        class="note-video-entry"
        data-test="note-video-entry"
      >
        <message-attachment-preview
          :local-file="entry"
          :upload="chipUpload(entry)"
          variant="compose"
          @upload-action="$emit('upload-action', { entry, action: $event })"
        />
        <!-- The chip's own buttons are Retry for a failure and none for a finished
             video, and a video that is not ready holds Save. Remove is the way out
             in every state: it is the chip's Cancel, which also discards a ready or
             failed upload and frees the entry's place toward the note's limit. -->
        <button
          type="button"
          class="note-video-remove"
          data-test="note-video-remove"
          :aria-label="`Remove ${entry.name}`"
          @click="$emit('upload-action', { entry, action: 'cancel' })"
        >
          Remove
        </button>
        <div
          class="note-video-visibility"
          data-test="note-video-visibility"
        >
          <label class="note-video-visibility-toggle">
            <input
              type="checkbox"
              role="switch"
              data-test="note-video-visible"
              :checked="entry.patientVisible"
              @change="$emit('visible-change', { entry, visible: $event.target.checked })"
            >
            <span
              class="note-video-visible-label"
              data-test="note-video-visible-label"
            >{{
              entry.patientVisible ? "Patient can see this video" : "Staff only"
            }}</span>
          </label>
          <p
            class="note-video-visible-help"
            data-test="note-video-visible-help"
          >
            Staff who can read this note can always play it. Turn this on to let the patient play it too.
          </p>
        </div>
      </div>
    </div>
    <p
      v-if="busy"
      class="note-video-busy"
      role="status"
      data-test="note-video-busy"
    >
      Save is available once your video has finished uploading.
    </p>
  </div>
</template>

<script>
import MessageAttachmentPreview from "@/components/private-layouts/message/MessageAttachmentPreview.vue";
import { NOTE_VIDEO_READY_TEXT } from "@/utils/noteAttachments";

export default {
  name: "NoteVideoAttach",
  components: { MessageAttachmentPreview },
  props: {
    // noteVideoMixin's entries: { id, name, size, type, upload, patientVisible }.
    entries: { type: Array, default: () => [] },
    // The server's video types and extensions, and nothing else.
    accept: { type: String, default: "" },
    // The note already has as many videos as the server allows.
    full: { type: Boolean, default: false },
    // A video is not ready: Save is waiting for it.
    busy: { type: Boolean, default: false },
  },
  emits: ["pick", "upload-action", "visible-change"],
  methods: {
    choose() {
      this.$refs.input?.click();
    },
    // Copy the FileList before the input is reset: it is live and empties with the value.
    picked(event) {
      const files = Array.from(event?.target?.files || []);
      if (event?.target) event.target.value = "";
      if (files.length) this.$emit("pick", files);
    },
    // A finished video is not "ready to send": it is attached when the note is saved.
    chipUpload(entry) {
      const upload = entry.upload;
      return upload && upload.status === "ready" ? { ...upload, text: NOTE_VIDEO_READY_TEXT } : upload;
    },
  },
};
</script>

<style scoped>
.note-video-attach {
  margin: 8px 0;
  text-align: left;
}

.note-video-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
}

.note-video-button {
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  background-color: #ffffff;
  color: #1f2937;
  padding: 6px 12px;
  font-weight: 600;
  cursor: pointer;
}

.note-video-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.note-video-hint {
  font-size: 12px;
  opacity: 0.8;
  flex: 1 1 220px;
}

/* Many chips scroll here, so Save and the rest of the popup stay on screen. */
.note-video-list {
  max-height: 220px;
  overflow-y: auto;
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.note-video-entry {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 6px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #f9fafb;
}

.note-video-remove {
  align-self: flex-start;
  border: 1px solid #ced4da;
  border-radius: 6px;
  background: #ffffff;
  color: #495057;
  padding: 4px 10px;
  font-size: 13px;
  cursor: pointer;
}

.note-video-visibility-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  cursor: pointer;
}

.note-video-visible-help {
  margin: 0;
  font-size: 12px;
  opacity: 0.8;
}

.note-video-busy {
  margin: 6px 0 0;
  font-size: 12px;
  color: #b45309;
}
</style>
